import { apiBuilder } from "@zodios/core";
import { z } from "zod";
import { gvProductSchema } from "../../common/gvProductSchema";

const adSummarySchema = z.object({
  ad_id: z.number(),
  ad_name: z.string(),
  ad_status: z.number(),
  state: z.number(),
  sales: z.number(),
  spend: z.number(),
  ad_type: z.number(),
  daily_budget: z.number(),
  ad_goal: z.number(),
  non_trellis_enrolled_ad_accept_bids: z.boolean(),
  daily_budget_override: z.number().nullable(),
  daily_budget_override_valid_until: z.string().datetime().nullable(),
});

const hourlyDataSchema = z.record(
  z.string(),
  z.object({
    today_sales: z.number().optional(),
    today_units_ordered: z.number().optional(),
    yesterday_sales: z.number().optional(),
    yesterday_units_ordered: z.number().optional(),
    today_cost: z.number().optional(),
    today_ad_sales: z.number().optional(),
    today_impressions: z.number().optional(),
    today_view_impressions: z.number().optional(),
    today_clicks: z.number().optional(),
    today_attributed_conversions_14d: z.number().optional(),
    yesterday_cost: z.number().optional(),
    yesterday_ad_sales: z.number().optional(),
    yesterday_impressions: z.number().optional(),
    yesterday_view_impressions: z.number().optional(),
    yesterday_clicks: z.number().optional(),
    yesterday_attributed_conversions_14d: z.number().optional(),
  }),
);

export const liveDashboardApiDefinitions = apiBuilder({
  alias: "getLiveAdReportData",
  method: "get",
  path: "/merchandise/live_advertisements",
  parameters: [
    { name: "start_date", type: "Query", schema: z.string() },
    { name: "end_date", type: "Query", schema: z.string() },
    { name: "pre_start_date", type: "Query", schema: z.string() },
    { name: "pre_end_date", type: "Query", schema: z.string() },
    { name: "market_place", type: "Query", schema: z.string() },
    { name: "empty", type: "Query", schema: z.boolean() },
  ],
  status: 200,
  response: z.object({
    spend_by_type: z.object({
      SD: z.number().optional(),
      SP: z.number().optional(),
      SBV: z.number().optional(),
      SB: z.number().optional(),
      U: z.number().optional(),
    }),
    ad_results: z.array(adSummarySchema),
  }),
})
  .addEndpoint({
    alias: "getLiveAdSpendReportData",
    method: "get",
    path: "/merchandise/live_ad_spend_sales",
    parameters: [
      { name: "start_date", type: "Query", schema: z.string() },
      { name: "end_date", type: "Query", schema: z.string() },
      { name: "pre_start_date", type: "Query", schema: z.string() },
      { name: "pre_end_date", type: "Query", schema: z.string() },
      { name: "market_place", type: "Query", schema: z.string() },
      { name: "empty", type: "Query", schema: z.boolean() },
    ],
    status: 200,
    response: z.object({
      attributed_conversions_14d: z.number(),
      attributed_sales_7d: z.number(),
      clicks: z.number(),
      cost: z.number(),
      impressions: z.number(),
      view_impressions: z.number(),
    }),
  })
  .addEndpoint({
    alias: "getLiveOrdersData",
    method: "get",
    path: "/merchandise/live_orders",
    parameters: [
      { name: "start_date", type: "Query", schema: z.string() },
      { name: "end_date", type: "Query", schema: z.string() },
      { name: "pre_start_date", type: "Query", schema: z.string() },
      { name: "pre_end_date", type: "Query", schema: z.string() },
      { name: "market_place", type: "Query", schema: z.string() },
      { name: "empty", type: "Query", schema: z.boolean() },
    ],
    status: 200,
    response: z.object({
      quantity_ordered: z.number(),
      order_item_value: z.number(),
      products: z.array(gvProductSchema),
      top_products: z.array(gvProductSchema),
      top_units_sold: z.record(z.string(), z.number()),
    }),
  })
  .addEndpoint({
    alias: "getLiveOrdersHourlyData",
    method: "get",
    path: "/merchandise/live_data_by_hour",
    parameters: [
      { name: "start_date", type: "Query", schema: z.string() },
      { name: "end_date", type: "Query", schema: z.string() },
      { name: "pre_start_date", type: "Query", schema: z.string() },
      { name: "pre_end_date", type: "Query", schema: z.string() },
      { name: "market_place", type: "Query", schema: z.string() },
      { name: "empty", type: "Query", schema: z.boolean() },
    ],
    status: 200,
    response: hourlyDataSchema,
  })
  .build();

export type GVAdvertisementSummary = z.infer<typeof adSummarySchema>;
